import * as React from 'react';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import { Link, useNavigate } from 'react-router-dom';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import {
  DashboardIcon,
  HistoryIcon,
  MemoriesIcon,
  MenuIcon,
  Star02,
  Star03,
  SettingsIcon,
  HelpIcon,
} from './SVG';
import MenuButton from './MenuButton';
import { capitalizeFirstLetter } from 'utilities/index';
import { Box, Typography, LinearProgress, Grid } from '@mui/material';
import CreditsDots from './SVG/CreditsDots';
import {
  LinearProgressProps,
  linearProgressClasses,
} from '@mui/material/LinearProgress';
import { useResize } from 'hooks/common';
import { useCurrentUserAccount } from 'hooks/Account';
import { getDaysDifference } from 'utilities/time';
import { SPARK_ROUTES } from 'constants/routes';

const primaryListItem = [
  {
    id: 1,
    name: 'create',
    path: SPARK_ROUTES.SPARK_BUILDER,
    icon: <Star03 />,
    isCreateSparkRoute: true,
    hasSubMenu: true,
  },
  {
    id: 2,
    name: 'dashboard',
    path: '/dashboard',
    icon: <DashboardIcon />,
    hasSubMenu: false,
  },
  {
    id: 3,
    name: 'sparks',
    path: '/sparks',
    icon: <Star02 />,
    hasSubMenu: false,
  },
  {
    id: 4,
    name: 'Knowledge',
    path: '/sources',
    icon: <MemoriesIcon />,
    hasSubMenu: false,
  },
  {
    id: 5,
    name: 'history',
    path: '/history',
    icon: <HistoryIcon />,
    hasSubMenu: false,
  },
];
const secondaryListItem = [
  {
    id: 6,
    name: 'settings',
    path: '/settings/my-profile',
    icon: <SettingsIcon />,
  },
  {
    id: 7,
    name: 'help',
    path: '/help',
    icon: <HelpIcon />,
  },
];

const drawerWidth = '20rem';

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  boxShadow: theme.shadows[2],
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  [theme.breakpoints.down('sm')]: {
    width: 0,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  '& .MuiPaper-root': {
    border: 'none',
  },
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

function CustomizedProgressBar({
  vertical = false,
  sx = {},
  value,
}: {
  vertical?: boolean;
  sx?: LinearProgressProps['sx'];
} & LinearProgressProps) {
  return (
    <LinearProgress
      variant='determinate'
      value={value}
      sx={{
        transform: vertical ? 'rotate(-90deg)' : 'unset',
        height: 15,
        [`&.${linearProgressClasses.colorPrimary}`]: {
          backgroundColor: 'rgba(150,150,150,0.5)',
        },
        [`& .${linearProgressClasses.bar}`]: {
          backgroundColor: '#FFC000',
        },
        ...sx,
      }}
    />
  );
}

type MiniDrawerTypeProps = {
  open: boolean;
  toggleDrawer: () => void;
  headerHeight: number;
};

export default function SideNav({
  open,
  toggleDrawer,
  headerHeight,
}: MiniDrawerTypeProps) {
  const { isMobile } = useResize();
  const navigate = useNavigate();

  const { account } = useCurrentUserAccount();

  const accountMeta = account?.meta;
  const accountUsage = accountMeta?.subscription?.credits.usage;
  const total = accountUsage
    ? accountUsage?.currentIntervalUsage +
      accountUsage.remainingCreditsForCurrentInterval
    : 0;
  const accountUsagePercentage =
    !!accountUsage && total
      ? (accountUsage?.currentIntervalUsage / total) * 100
      : 0;
  const accountMetaCredits = accountMeta?.subscription?.credits;

  const handleSelect = (path: string) => {
    navigate(path);
    if (open) {
      toggleDrawer();
    }
  };
  return (
    <Drawer
      variant='permanent'
      open={open}
      sx={{
        '& .MuiDrawer-paper': {
          borderRight: (theme) => `1px solid ${theme.palette.borderColor}`,
          maxWidth: isMobile ? '100%' : '50%',
          zIndex: 100,
          height: `calc(100vh - ${headerHeight}px)`,
          marginTop: `${headerHeight}px`,

          scrollbarWidth: 'none',
        },
      }}
    >
      <Grid
        container
        height='100%'
        direction='column'
        justifyContent='space-between'
        flexWrap='nowrap'
      >
        <Grid item container direction='column'>
          {!isMobile && (
            <Box display='flex' justifyContent={open ? 'flex-end' : 'center'}>
              <IconButton onClick={toggleDrawer}>
                <MenuIcon />
              </IconButton>
            </Box>
          )}
          {primaryListItem.map(
            ({ name, id, icon, path, isCreateSparkRoute, hasSubMenu }) => (
              <MenuButton
                key={id}
                open={open}
                icon={icon}
                path={path}
                text={capitalizeFirstLetter(name)}
                name={name}
                isCreateSparkRoute={isCreateSparkRoute}
                hasSubMenu={hasSubMenu}
                onSelect={handleSelect}
              />
            )
          )}
        </Grid>
        <Grid item container direction='column'>
          <Grid item>
            <Divider sx={{ margin: '0 1rem 0 1rem' }} />
          </Grid>
          <Box>
            {secondaryListItem.map(({ name, id, icon, path }) => (
              <MenuButton
                key={id}
                open={open}
                text={capitalizeFirstLetter(name)}
                name={name}
                path={path}
                icon={icon}
                onSelect={handleSelect}
              />
            ))}
          </Box>
          {!open && (
            <Box
              display='flex'
              flexDirection={'column'}
              height='162px'
              borderRadius={3}
              margin={2}
              gap={0.5}
              position='relative'
            >
              <CustomizedProgressBar
                value={accountUsagePercentage}
                vertical={true}
                sx={{
                  width: '160px',
                  position: 'absolute',
                  left: -60,
                  top: '50%',
                  border: '1px solid rgb(202 202 202)',
                }}
              />
            </Box>
          )}
          {open && (
            <Box
              display='flex'
              flexDirection={'column'}
              width='90%'
              height='162px'
              borderRadius={3}
              margin={2}
              gap={0.5}
              sx={{ bgcolor: (theme) => theme.palette.primary.main }}
            >
              <Box
                display={'flex'}
                padding='16px 16px 0 16px'
                justifyContent='space-between'
                alignItems='center'
                fontSize='18px'
              >
                <Typography color='white' variant='h6'>
                  Usage
                </Typography>
                <Box>
                  <CreditsDots />
                </Box>
              </Box>
              <Box
                paddingX={2}
                display='flex'
                flexDirection='column'
                gap={1}
                flex={1}
              >
                <CustomizedProgressBar value={accountUsagePercentage} />
              </Box>
              <Box
                padding={2}
                display='flex'
                gap={1}
                flexWrap='wrap'
                justifySelf='flex-end'
              >
                {accountMetaCredits?.nextIssueDate && (
                  <Typography variant='body2' color={'white'} fontSize='12px'>
                    Credits reset in{' '}
                    <span>
                      {getDaysDifference(accountMetaCredits?.nextIssueDate)}
                    </span>{' '}
                    days.
                  </Typography>
                )}
                <Link
                  style={{ cursor: 'pointer', color: 'white' }}
                  to='/settings/billing-and-usage'
                >
                  <Typography variant='body2' color={'white'} fontSize='12px'>
                    Manage plan
                  </Typography>
                </Link>
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
    </Drawer>
  );
}
